"use strict";
import { useEffect, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { LoadingIndicator } from "@/Common/Components/Loader/LoadingIndicator";
import { CallCenterHelper } from "@/Common/Helpers/CallCenterHelper";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { CallCenterCredentials } from "./CallCenterCredentials";
import { CallCenterLoginAndStatus } from "./CallCenterLoginAndStatus";
import { CallCenterOptions } from "./CallCenterOptions";
import { CallCenterOptionsState, CallCenterUserCredentialsState, CallCenterUserSettingsState, GetUserCallCenterSettingsResponse, ProviderTypes, UpdateUserCallCenterCredentialsRequest, UpdateUserCallCenterCredentialsResponse, UpdateUserCallCenterOptionsRequest } from "./CallCenterSettings.types";

import styles from "./CallCenterSettings.module.scss";

import img_loading from "@/Globals/_images/loading.gif";

function CallCenterSettings(): JSX.Element {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [settings, setSettings] = useState<CallCenterUserSettingsState>(null);

    async function saveCredentials(savingCredentials: CallCenterUserCredentialsState): Promise<void> {
        const requestBody: UpdateUserCallCenterCredentialsRequest = { Credentials: savingCredentials != null ? { ...savingCredentials } : null };
        const response = await Client.postAsync<UpdateUserCallCenterCredentialsResponse>(ServerOperations.Server.Myle_CallCenter_UpdateUserCredentials, requestBody);

        if (!response.isSuccess) {
            sessionStore.showPopup(CallCenterHelper.formatErrorMessages(response.errors, savingCredentials?.Provider));

            // remove secret from state before logging
            const debugState = savingCredentials == null ? null : {
                ...savingCredentials,
                Secret: savingCredentials.Secret != null ? ("was provided with length " + savingCredentials.Secret.length) : "was null"
            };
            console.error("Error Saving CallCenterCredentials.", response.errors, debugState);

            throw response.errors;
        }

        const savedCredentials = CallCenterUserCredentialsState.fromResponse(response.data.Credentials);
        const addedCredentials = settings?.Credentials == null && savedCredentials != null;
        setSettings(settings => ({
            ...settings,
            Credentials: savedCredentials
        }));

        if (addedCredentials) {
            const defaultOptions = new CallCenterOptionsState({
                Active: true,
                AskForAccountAtLogin: false,
                AutoOpenPatientOnSingleMatch: settings?.Options?.AutoOpenPatientOnSingleMatch ?? false
            });
            await saveOptions(defaultOptions);
        }

        if (savedCredentials == null) {
            await CallCenterHelper.logoutAsync();
        }
        else {
            // if the user isnt logged in, it will ignore logging out.
            await CallCenterHelper.logoutAsync();
            await CallCenterHelper.loginAsync({ isUserInitiated: true });
        }
    }

    async function saveOptions(savingOptions: CallCenterOptionsState): Promise<void> {
        const requestBody: UpdateUserCallCenterOptionsRequest = { ...savingOptions };
        const response = await Client.postAsync<void>(ServerOperations.Server.Myle_CallCenter_UpdateUserOptions, requestBody);

        if (!response.isSuccess) {
            sessionStore.showPopup(CallCenterHelper.formatErrorMessages(response.errors, settings?.Credentials?.Provider));

            console.error("Error Saving CallCenterOptions.", response.errors, savingOptions);

            throw response.errors;
        }

        // nothing is returned, use copy of existing request object if successful
        const savedOptions = { ...savingOptions };
        setSettings(settings => ({
            ...settings,
            Options: savedOptions
        }));

        if (savedOptions.AutoOpenPatientOnSingleMatch != settings.Options.AutoOpenPatientOnSingleMatch) {
            // update store
            sessionStore.CallCenter_AutoOpenPatientOnSingleMatch = savedOptions.AutoOpenPatientOnSingleMatch;
        }
    }

    function onRetry(): void {
        setError(false);
        setLoading(true);
    }

    useEffect(() => {
        if (!loading) {
            return;
        }
        if (error) {
            return;
        }
        async function fetchData(): Promise<void> {
            const response = await Client.postAsync<GetUserCallCenterSettingsResponse>(ServerOperations.Server.Myle_CallCenter_GetUserSettings, null);
            if (loading) {
                if (!response.isSuccess) {
                    setError(true);
                    console.error("Error loading CallCenterSettings.", response.errors);
                }
                else {
                    setSettings(CallCenterUserSettingsState.fromResponse(response.data));
                }
                setLoading(false);
            }
        }
        CallCenterHelper.closeLoginWindow();
        fetchData();
    }, [loading, error]);

    if (error) {
        const failedText = cacheStore.GetString("CallCenter_FailedToLoad");
        const retryText = cacheStore.GetString("RetryLabel");
        return (
            <div className={styles.container}>
                <div className={styles.failed}>
                    <div className={styles.errorMessage}>{failedText}</div>
                    <a onClick={onRetry}>{retryText}</a>
                </div>
            </div>
        );
    }
    if (settings == null) {
        return (
            <div className={styles.container}>
                <LoadingIndicator imagePath={img_loading} />
            </div>
        );
    }

    const showLoginSettings = settings.Credentials != null;

    return (
        <div className={`col-sm-12 col-md-10 col-lg-10 ${styles.container}`}>
            {showLoginSettings && <CallCenterLoginAndStatus />}
            <CallCenterCredentials
                credentials={settings.Credentials}
                saveCredentials={saveCredentials}
            />
            {showLoginSettings && (
                <CallCenterOptions
                    providerType={settings.Credentials?.Provider ?? ProviderTypes.ComUnik}
                    options={settings.Options}
                    saveOptions={saveOptions}
                />
            )}

        </div>
    );
}

export { CallCenterSettings };
